import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShoppingCart, Mail, MapPin, Phone, X, Plus, Minus, Trash2, ArrowRight, Search, ShoppingBag, CreditCard, Banknote, Truck, Check, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CartDrawer({ open, onClose, cart, setCart, subdomain, currency, paymentConfig }) {
  const [step, setStep] = useState("cart"); // cart | checkout | pay | done
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", shipping_address: "" });
  const [method, setMethod] = useState("");
  const [orderResp, setOrderResp] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!open) return null;
  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);

  const updateQty = (id, delta) => {
    setCart((c) => c.map((i) => i.id === id ? { ...i, qty: Math.max(1, i.qty + delta) } : i));
  };
  const remove = (id) => setCart((c) => c.filter((i) => i.id !== id));

  const enabledMethods = [
    paymentConfig?.paystack?.enabled ? "paystack" : null,
    paymentConfig?.bank_transfer?.enabled ? "bank_transfer" : null,
    paymentConfig?.cod?.enabled ? "cod" : null,
  ].filter(Boolean);

  const placeOrder = async () => {
    if (!form.customer_name || !form.customer_email) return toast.error("Name & email required");
    if (!method) return toast.error("Choose a payment method");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/store/${subdomain}/order`, { ...form, items: cart, payment_method: method });
      setOrderResp(r.data);
      // Paystack — initialize transaction & redirect
      if (method === "paystack") {
        const callback = `${window.location.origin}/store/${subdomain}?paystack_ref={{REF}}`;
        const init = await axios.post(`${API}/store/${subdomain}/order/paystack/init`, {
          order_id: r.data.order_id,
          callback_url: `${window.location.origin}/store/${subdomain}?ps_order=${r.data.order_id}`,
        });
        if (init.data.authorization_url) {
          // Persist a small marker so callback page can show the result
          localStorage.setItem(`brivo_pending_order_${subdomain}`, JSON.stringify({ ref: init.data.reference, order_number: r.data.order_number }));
          window.location.href = init.data.authorization_url;
          return;
        }
      }
      // Bank transfer / COD — show instructions
      setCart([]);
      localStorage.removeItem(`brivo_cart_${subdomain}`);
      setStep("done");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-md bg-white h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-black/8 flex items-center justify-between">
          <h2 className="font-display font-bold text-xl text-black">
            {step === "cart" ? "Your cart" : step === "checkout" ? "Checkout" : step === "pay" ? "Payment" : "Order placed!"}
          </h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-black/5"><X size={18} /></button>
        </div>

        {step === "cart" && (
          <div className="p-5">
            {cart.length === 0 ? (
              <div className="text-center py-16 text-black/45">
                <ShoppingBag size={42} className="mx-auto mb-3 opacity-40" />
                <div>Your cart is empty</div>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {cart.map((i) => (
                    <div key={i.id} className="flex items-center gap-3 p-3 rounded-xl border border-black/8">
                      <div className="w-14 h-14 rounded-lg bg-[#F4F5FA] flex items-center justify-center text-xs font-bold text-black/45">
                        {i.image_url ? <img src={`${process.env.REACT_APP_BACKEND_URL}${i.image_url}`} alt="" className="w-full h-full object-cover rounded-lg" /> : i.name.slice(0,2)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-black truncate">{i.name}</div>
                        <div className="text-xs text-black/55">{currency}{Number(i.price).toLocaleString()}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => updateQty(i.id, -1)} className="p-1 rounded bg-black/5 hover:bg-black/10"><Minus size={11} /></button>
                        <span className="w-7 text-center font-bold text-sm">{i.qty}</span>
                        <button onClick={() => updateQty(i.id, 1)} className="p-1 rounded bg-black/5 hover:bg-black/10"><Plus size={11} /></button>
                      </div>
                      <button onClick={() => remove(i.id)} className="p-1.5 rounded text-rose-500 hover:bg-rose-50"><Trash2 size={12} /></button>
                    </div>
                  ))}
                </div>
                <div className="mt-5 pt-4 border-t border-black/10 flex items-center justify-between">
                  <span className="text-sm text-black/55">Total</span>
                  <span className="font-display font-extrabold text-2xl text-black">{currency}{total.toLocaleString()}</span>
                </div>
                <button onClick={() => setStep("checkout")} data-testid="cart-checkout"
                  className="mt-4 w-full py-3 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white font-bold inline-flex items-center justify-center gap-2">
                  Checkout <ArrowRight size={14} />
                </button>
              </>
            )}
          </div>
        )}

        {step === "checkout" && (
          <div className="p-5 space-y-3">
            {[
              ["customer_name", "Full name"],
              ["customer_email", "Email"],
              ["customer_phone", "Phone"],
              ["shipping_address", "Shipping address"],
            ].map(([k, lbl]) => (
              <div key={k}>
                <label className="text-xs font-bold uppercase tracking-wider text-black/55">{lbl}</label>
                <input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  data-testid={`co-${k}`}
                  className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1FA868] outline-none text-sm" />
              </div>
            ))}
            <div className="pt-2">
              <div className="text-xs font-bold uppercase tracking-wider text-black/55 mb-2">Payment Method</div>
              {enabledMethods.length === 0 && (
                <div className="rounded-lg p-3 bg-amber-50 text-amber-800 text-xs">
                  This store hasn't enabled any payment methods yet.
                </div>
              )}
              <div className="space-y-2">
                {paymentConfig?.paystack?.enabled && (
                  <button onClick={() => setMethod("paystack")} data-testid="pm-opt-paystack"
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition ${method === "paystack" ? "border-[#1FA868] bg-[#F0FAF3] shadow-sm" : "border-black/10 hover:border-black/25"}`}>
                    <CreditCard size={18} className="text-[#1FA868]" />
                    <div className="flex-1">
                      <div className="font-bold text-sm">Pay with Card / Paystack</div>
                      <div className="text-[11px] text-black/55">Secure online payment</div>
                    </div>
                    {method === "paystack" && <Check size={16} className="text-[#1FA868]" />}
                  </button>
                )}
                {paymentConfig?.bank_transfer?.enabled && (
                  <button onClick={() => setMethod("bank_transfer")} data-testid="pm-opt-bank"
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition ${method === "bank_transfer" ? "border-[#1FA868] bg-[#F0FAF3] shadow-sm" : "border-black/10 hover:border-black/25"}`}>
                    <Banknote size={18} className="text-[#1FA868]" />
                    <div className="flex-1">
                      <div className="font-bold text-sm">Bank Transfer</div>
                      <div className="text-[11px] text-black/55">Transfer to {paymentConfig.bank_transfer.bank_name || "our bank"}</div>
                    </div>
                    {method === "bank_transfer" && <Check size={16} className="text-[#1FA868]" />}
                  </button>
                )}
                {paymentConfig?.cod?.enabled && (
                  <button onClick={() => setMethod("cod")} data-testid="pm-opt-cod"
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition ${method === "cod" ? "border-[#1FA868] bg-[#F0FAF3] shadow-sm" : "border-black/10 hover:border-black/25"}`}>
                    <Truck size={18} className="text-[#1FA868]" />
                    <div className="flex-1">
                      <div className="font-bold text-sm">Pay on Delivery</div>
                      <div className="text-[11px] text-black/55">Pay the courier in cash on arrival</div>
                    </div>
                    {method === "cod" && <Check size={16} className="text-[#1FA868]" />}
                  </button>
                )}
              </div>
            </div>
            <div className="pt-3 border-t border-black/10 flex items-center justify-between">
              <span className="text-sm text-black/55">Total</span>
              <span className="font-bold text-lg">{currency}{total.toLocaleString()}</span>
            </div>
            <button onClick={placeOrder} disabled={busy || !method || enabledMethods.length === 0} data-testid="co-place"
              className="w-full py-3 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white font-bold disabled:opacity-60">
              {busy ? "Placing order…" : (method === "paystack" ? "Pay now" : "Place order")}
            </button>
          </div>
        )}

        {step === "done" && orderResp && (
          <div className="p-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mx-auto flex items-center justify-center"><Check size={28} /></div>
              <div className="font-display font-bold text-xl text-black mt-4">Order placed</div>
              <div className="text-sm text-black/55 mt-1">Reference: <span className="font-mono font-bold">{orderResp.order_number}</span></div>
            </div>
            {orderResp.payment_method === "bank_transfer" && paymentConfig?.bank_transfer?.enabled && (
              <div className="mt-5 p-4 rounded-xl bg-[#F0FAF3] border border-[#1FA868]/30">
                <div className="font-bold text-sm text-[#0F5132] mb-2">Bank Transfer Details</div>
                <div className="space-y-1.5 text-sm">
                  <KV k="Bank" v={paymentConfig.bank_transfer.bank_name} />
                  <KV k="Account Holder" v={paymentConfig.bank_transfer.account_holder} />
                  <KV k="Account Number" v={paymentConfig.bank_transfer.account_number} copy />
                  <KV k="Amount" v={`${currency}${orderResp.total.toLocaleString()}`} />
                  <KV k="Reference" v={orderResp.order_number} copy />
                </div>
                {paymentConfig.bank_transfer.instructions && (
                  <div className="mt-3 text-xs text-black/65 whitespace-pre-wrap">{paymentConfig.bank_transfer.instructions}</div>
                )}
              </div>
            )}
            {orderResp.payment_method === "cod" && (
              <div className="mt-5 p-4 rounded-xl bg-amber-50 border border-amber-200">
                <div className="font-bold text-sm text-amber-900 mb-1">Pay on Delivery</div>
                <div className="text-xs text-amber-900/80">{paymentConfig?.cod?.instructions}</div>
                <div className="text-xs text-black/65 mt-2">We'll send you a confirmation email shortly.</div>
              </div>
            )}
            <button onClick={() => { setStep("cart"); onClose(); }}
              className="mt-5 w-full py-2.5 rounded-xl bg-black text-white text-sm font-bold">Continue shopping</button>
          </div>
        )}
      </div>
    </div>
  );
}

function KV({ k, v, copy }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-black/55">{k}</span>
      <span className="font-mono font-semibold text-sm flex items-center gap-1.5">
        {v}
        {copy && (
          <button onClick={() => { navigator.clipboard?.writeText(String(v)); toast.success("Copied"); }}
            className="text-black/40 hover:text-black"><Copy size={12} /></button>
        )}
      </span>
    </div>
  );
}

export default function PublicStorefront() {
  const { subdomain } = useParams();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [search, setSearch] = useState("");
  const [cartOpen, setCartOpen] = useState(false);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`brivo_cart_${subdomain}`) || "[]"); } catch { return []; }
  });
  const [news, setNews] = useState({ email: "", busy: false });

  useEffect(() => {
    axios.get(`${API}/store/${subdomain}`).then((r) => setData(r.data)).catch((e) => {
      toast.error(e.response?.data?.detail || "Store not found");
    });
    axios.get(`${API}/store/${subdomain}/payment-config`).then((r) => setPaymentConfig(r.data)).catch(() => {});
  }, [subdomain]);

  // Handle Paystack redirect-back: ?reference=xxx OR ?trxref=xxx
  useEffect(() => {
    const ref = params.get("reference") || params.get("trxref");
    if (!ref) return;
    axios.get(`${API}/store/${subdomain}/order/paystack/verify/${ref}`).then((r) => {
      if (r.data.status === "success") {
        toast.success(`Payment confirmed · ${r.data.order_number}`);
        localStorage.removeItem(`brivo_cart_${subdomain}`);
        setCart([]);
      } else {
        toast.error("Payment was not completed");
      }
    }).catch(() => toast.error("Could not verify payment")).finally(() => {
      // Clean URL
      const sp = new URLSearchParams(params);
      sp.delete("reference"); sp.delete("trxref"); sp.delete("ps_order");
      setParams(sp, { replace: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subdomain]);

  useEffect(() => { localStorage.setItem(`brivo_cart_${subdomain}`, JSON.stringify(cart)); }, [cart, subdomain]);

  if (!data) return <div className="min-h-screen flex items-center justify-center text-black/55">Loading store…</div>;
  const { settings, products, banners, tenant } = data;
  const primary = settings.primary_color || "#1B4332";
  const secondary = settings.secondary_color || "#1FA868";
  const symbol = settings.currency === "USD" ? "$" : settings.currency === "EUR" ? "€" : settings.currency === "GBP" ? "£" : "₦";

  const addToCart = (p) => {
    setCart((c) => {
      const found = c.find((i) => i.id === p.id);
      if (found) return c.map((i) => i.id === p.id ? { ...i, qty: i.qty + 1 } : i);
      return [...c, { id: p.id, name: p.name, price: p.price, image_url: p.image_url, qty: 1 }];
    });
    toast.success("Added to cart");
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!news.email.includes("@")) return toast.error("Valid email required");
    setNews({ ...news, busy: true });
    try {
      await axios.post(`${API}/store/${subdomain}/subscribe`, { email: news.email });
      toast.success("Subscribed!");
      setNews({ email: "", busy: false });
    } catch (e) { toast.error("Failed"); setNews({ ...news, busy: false }); }
  };

  const filtered = products.filter((p) => !search || p.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="public-storefront" className="min-h-screen bg-white" style={{ fontFamily: settings.font_family || "Inter, system-ui, sans-serif" }}>
      {/* Topbar */}
      <div className="text-white text-xs py-2 px-4 flex items-center justify-between" style={{ background: primary }}>
        <span>{settings.topbar_left}</span>
        <span className="hidden md:inline">{settings.topbar_right}</span>
      </div>

      {/* Header */}
      <header className="border-b border-black/8 sticky top-0 bg-white/95 backdrop-blur z-30">
        <div className="max-w-7xl mx-auto px-5 py-4 flex items-center gap-4">
          <div className="flex items-center gap-3">
            {settings.logo_url ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${settings.logo_url}`} alt="" className="h-9" />
            ) : (
              <div className="font-display font-extrabold text-xl" style={{ color: primary }}>{settings.store_name || tenant.name}</div>
            )}
          </div>
          <div className="hidden md:flex flex-1 items-center gap-2 max-w-md mx-auto bg-[#F4F5FA] rounded-full px-4 py-2.5">
            <Search size={14} className="text-black/40" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products…"
              data-testid="store-search"
              className="flex-1 bg-transparent focus:outline-none text-sm" />
          </div>
          <button onClick={() => setCartOpen(true)} data-testid="store-cart-btn"
            className="relative inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-white font-bold text-sm"
            style={{ background: primary }}>
            <ShoppingCart size={14} />
            <span className="hidden sm:inline">Cart</span>
            {cart.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
                {cart.reduce((s, i) => s + i.qty, 0)}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Hero */}
      <section
  className="relative overflow-hidden"
  style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` }}
>
  <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_top_left,white,transparent)]" />

  <div className="max-w-7xl mx-auto px-5 py-20 grid md:grid-cols-2 gap-10 items-center">

    <div className="text-white">
      <h1 className="font-display font-extrabold text-4xl lg:text-6xl leading-tight">
        {settings.hero_title}
      </h1>

      <p className="mt-5 text-white/85 text-lg max-w-md">
        {settings.hero_subtitle}
      </p>

      <a
        href="#products"
        className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black font-bold shadow-md hover:scale-[1.02] transition"
      >
        Shop now <ArrowRight size={14} />
      </a>
    </div>

    {settings.hero_image && (
      <div className="rounded-3xl overflow-hidden shadow-2xl">
        <img
          src={`${process.env.REACT_APP_BACKEND_URL}${settings.hero_image}`}
          className="w-full h-[350px] object-cover"
        />
      </div>
    )}

  </div>
</section>

      {/* Banners */}
      {banners?.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 py-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {banners.map((b) => (
            <a key={b.id} href={b.link_url || "#products"}
              className="rounded-2xl overflow-hidden bg-[#F4F5FA] aspect-[16/10] relative group">
              {b.image_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${b.image_url}`} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
              <div className="absolute inset-0 bg-gradient-to-tr from-black/60 to-transparent flex items-end p-5">
                <div className="text-white">
                  <div className="font-display font-bold text-xl">{b.title}</div>
                  <div className="text-sm text-white/85">{b.subtitle}</div>
                </div>
              </div>
            </a>
          ))}
        </section>
      )}

      {/* Products */}
      <section id="products" className="max-w-7xl mx-auto px-5 py-12">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display font-extrabold text-3xl text-black">Shop products</h2>
          <span className="text-sm text-black/55">{filtered.length} products</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {filtered.map((p) => (
           <div key={p.id} className="group relative">

           <div className="aspect-square rounded-2xl bg-[#F7F8FB] overflow-hidden relative shadow-sm group-hover:shadow-lg transition">
         
             {p.image_url ? (
               <img
                 src={`${process.env.REACT_APP_BACKEND_URL}${p.image_url}`}
                 className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
               />
             ) : (
               <div className="flex items-center justify-center h-full text-black/20 font-bold text-3xl">
                 {p.name.slice(0, 2)}
               </div>
             )}
         
             {/* Floating Add button */}
             <button
               onClick={() => addToCart(p)}
               className="absolute bottom-3 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full text-white text-xs font-bold opacity-0 group-hover:opacity-100 transition shadow-md"
               style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})` }}
             >
               <Plus size={12} className="inline mr-1" />
               Add to cart
             </button>
           </div>
         
           <div className="mt-3">
             <div className="font-semibold text-sm text-black line-clamp-2">
               {p.name}
             </div>
         
             <div className="font-display font-extrabold text-lg mt-1">
               {symbol}{Number(p.price).toLocaleString()}
             </div>
           </div>
         
         </div>
          ))}
        </div>
        {filtered.length === 0 && <div className="py-16 text-center text-black/45">No products match your search.</div>}
      </section>

      {/* Newsletter */}
      {settings.homepage_blocks?.find((b) => b.id === "newsletter" && b.active) !== undefined && (
        <section className="max-w-3xl mx-auto px-5 py-16 text-center">
          <h3 className="font-display font-extrabold text-3xl text-black">Stay in the loop</h3>
          <p className="text-black/55 mt-2">Get the latest drops & promos in your inbox.</p>
          <form onSubmit={subscribe} className="mt-6 flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
            <input type="email" value={news.email} onChange={(e) => setNews({ ...news, email: e.target.value })}
              placeholder="you@example.com" required data-testid="newsletter-email"
              className="flex-1 px-4 py-3 rounded-xl border border-black/15 focus:border-[#1FA868] outline-none text-sm" />
            <button disabled={news.busy} data-testid="newsletter-submit"
              className="px-5 py-3 rounded-xl text-white font-bold text-sm disabled:opacity-60"
              style={{ background: primary }}>
              {news.busy ? "…" : "Subscribe"}
            </button>
          </form>
        </section>
      )}

      {/* Footer */}
      <footer className="text-white py-10 px-5" style={{ background: primary }}>
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-6">
          <div>
            <div className="font-display font-extrabold text-2xl">{settings.store_name}</div>
            <p className="text-white/70 text-sm mt-2">{settings.footer_text}</p>
          </div>
          <div>
            <div className="font-bold text-sm uppercase tracking-wider text-white/85">Contact</div>
            <div className="mt-3 space-y-1.5 text-sm text-white/75">
              {settings.contact_email && <div className="flex items-center gap-2"><Mail size={12} /> {settings.contact_email}</div>}
              {settings.contact_phone && <div className="flex items-center gap-2"><Phone size={12} /> {settings.contact_phone}</div>}
              {settings.contact_address && <div className="flex items-center gap-2"><MapPin size={12} /> {settings.contact_address}</div>}
            </div>
          </div>
          <div>
            <div className="font-bold text-sm uppercase tracking-wider text-white/85">Follow</div>
            <div className="mt-3 flex gap-2">
              {(settings.social_links || []).map((l) => (
                <a key={l.id} href={l.url} target="_blank" rel="noopener noreferrer"
                  className="px-3 py-1.5 rounded-full bg-white/15 text-xs font-bold capitalize hover:bg-white/25">{l.platform}</a>
              ))}
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto pt-6 mt-6 border-t border-white/15 flex justify-between text-xs text-white/55">
          <span>© {new Date().getFullYear()} {settings.store_name}</span>
          <span>Powered by Brivo360</span>
        </div>
      </footer>

      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} cart={cart} setCart={setCart} subdomain={subdomain} currency={symbol} paymentConfig={paymentConfig} />
    </div>
  );
}
